import { traverseProperty } from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { hasValue, today } from "@repo/helpers";
import { keyPair, SerializedFile } from "@repo/models";
import { useCallback, useMemo } from "react";

import { useDrugTestApi } from "../../../api/drugTestApi";
import { useRepOnboardingQuery, useTabCountQuery } from "../../../api/repApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useDrugTestsContext, useRepContext } from "../../../contexts/editableItemContext";
import { buildSaveChain } from "../../../helpers/saveChain";
import { DrugTest } from "../../../models";
import { tabs } from "../../../string";

export default function DrugTestsEditorDynamic(props: Props) {
	const { repId } = useRepContext();
	const context = useDrugTestsContext();
	const drugTest = context.item;
	const [drugTestLists, drugTestListsLoaded] = useDrugTestLists();
	const { saveDrugTestDocs, loadDrugTest, saveDrugTest, useDrugTestQuery } = useDrugTestApi();
	const { clearQuery } = useDrugTestQuery(repId);
	const { checkOnboarding } = useRepOnboardingQuery();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);

	const templateData = useMemo(
		() => ({
			drugTest,
			drugTestLists,
		}),
		[drugTest, drugTestLists],
	);

	const save = useCallback(
		() =>
			buildSaveChain(saveDrugTest, "Saving Drug Test")
				.thenRun((id: number) => saveDrugTestDocs(id, drugTest), "Saving Documents")
				.then(loadDrugTest, "Loading...")
				.thenRun(() => checkOnboarding())
				.thenRun(clearQuery)
				.thenRun(clearTabCount)
				.execute(drugTest),
		[drugTest, saveDrugTest, loadDrugTest, saveDrugTestDocs],
	);

	const updateValues = useCallback(updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(id: string, value: any, currentState: IState, parentComponent?: string) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}

		const clear = async (name: string) => await update(name, null);

		//Custom logic
		switch (id) {
			case "drugTest.responseStarted":
				if (value && !hasValue(newState.drugTest.secondTestConductedOn))
					await update("drugTest.secondTestConductedOn", today());
				else await update("drugTest.secondTestConductedOn", null);
				break;
			case "drugTest.resultDocument":
			case "drugTest.secondTestResultDocument":
				const existingProp = id + "Uploaded";
				const existing = traverseProperty(existingProp, newState) as SerializedFile[];
				if (existing?.length) await clear(existingProp);
				break;
		}

		return newState as IState;
	}

	//ZZT: Hack to get fix issues with multiselect ddl values being reset
	if (!drugTest) return null;
	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.drugTests}
			otherTemplateData={templateData}
			readonly={props.readOnly}
			itemProp="drugTest"
			updateValues={updateValues}
			save={save}
			loaded={drugTestListsLoaded}
		/>
	);
}

interface IState extends ITemplateData {
	drugTest: DrugTest;
}

const TEST_TYPES = [
	{ id: 1, value: "Quick" },
	{ id: 2, value: "Lab" },
];
const TEST_RESULT = [
	{ id: 1, value: "Positive" },
	{ id: 2, value: "Negative" },
];

const EXCEPTION_STATUS = [
	{ id: 3, value: "Approved" },
	{ id: 2, value: "Denied" },
	{ id: 1, value: "Pending" },
];

export function useDrugTestLists() {
	const { repId } = useRepContext();
	const { useOnboardingSegmentsQuery } = useRepSegmentsApi(repId);
	const { onboardingSegments, loaded: representativeSegmentsLoaded } = useOnboardingSegmentsQuery();
	const loaded = representativeSegmentsLoaded;
	const activeLists = useMemo(() => {
		return {
			types: TEST_TYPES,
			results: TEST_RESULT,
			exceptionStatus: EXCEPTION_STATUS,
			onboardingSegments,
		};
	}, [onboardingSegments]);

	return [activeLists, loaded] as [IBackgroundCheckList, boolean];
}

interface IBackgroundCheckList {
	types: keyPair[];
	results: keyPair[];
	exceptionStatus: keyPair[];
	onboardingSegments: keyPair[];
}
interface Props {
	readOnly?: boolean;
}
